import React from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { TranslatedText } from "@plex/culture-react";
import { Dialog, SelectionMode, DataTable, Form, CancelLink, Text, DataTablePlugins } from "@plex/react-components";

import { isLocalizable } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { IValidationIssue } from "infrastructure/validation";
import { Banner } from "ui/app";
import { DialogContainer } from "ui/app/navigation";
import { useLocalizedValues } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messagesUploadActions } from "./MessagesUpload.actions";
import styles from "./MessagesUpload.module.scss";
import { IMessagesUploadValidationIssue, IMessagesUploadError, IMessagesUploadState } from "./MessagesUpload.state";

export interface IMessagesUploadResult {
  error?: IMessagesUploadError;
  notifications: INotification[];
  resetState: () => void;
}

export const MessagesUploadResult: React.FC<IMessagesUploadResult> = ({ error, notifications, resetState }) => {
  const localizedValues = useLocalizedValues(() => {
    return {
      messageId: displayMessages.property("messagesUploadResultItemMessageId"),
      text: displayMessages.property("messagesUploadResultItemText"),
      token: displayMessages.property("messagesUploadResultItemToken"),
      tokenDescription: displayMessages.property("messagesUploadResultItemTokenDescription"),
      validationResult: displayMessages.property("messagesUploadResultItemResult"),
      validationResultSectionHeader: displayMessages.heading("messagesUploadValidationResults"),
      dialogTitle: displayMessages.title("messagesUploadValidationResult")
    };
  });

  const getMessage = (issue: IValidationIssue) => {
    if (isLocalizable(issue.message)) {
      return <TranslatedText messageId={issue.message.messageId.value} context={issue.message.tokens} />;
    } else {
      return issue.message;
    }
  };

  return (
    <DialogContainer
      onHidden={resetState}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              {error && (
                <Form onSubmit={() => {}}>
                  <Form.Body>
                    <Form.Section title={localizedValues.validationResultSectionHeader}>
                      <Form.Row>
                        {error.validationIssues ? (
                          <DataTable<IMessagesUploadValidationIssue>
                            data={error.validationIssues}
                            keySelector={(row: IMessagesUploadValidationIssue) => row.uniqueId}
                            plugins={[
                              DataTablePlugins.Selections<IMessagesUploadValidationIssue>({
                                selected: [],
                                mode: SelectionMode.none
                              }),
                              DataTablePlugins.Loader()
                            ]}
                          >
                            <DataTable.Header>
                              <DataTable.HeaderRow>
                                <DataTable.HeaderCell key="1" sortable>
                                  {localizedValues.messageId}
                                </DataTable.HeaderCell>
                                <DataTable.HeaderCell key="2" sortable>
                                  {localizedValues.text}
                                </DataTable.HeaderCell>
                                <DataTable.HeaderCell key="3" sortable>
                                  {localizedValues.token}
                                </DataTable.HeaderCell>
                                <DataTable.HeaderCell key="4" sortable>
                                  {localizedValues.tokenDescription}
                                </DataTable.HeaderCell>
                                <DataTable.HeaderCell key="5" sortable>
                                  {localizedValues.validationResult}
                                </DataTable.HeaderCell>
                              </DataTable.HeaderRow>
                            </DataTable.Header>

                            <DataTable.Body<IMessagesUploadValidationIssue>>
                              {(row, rowIndex, rowInfo) => {
                                const key = rowInfo && rowInfo.key;

                                return (
                                  <DataTable.Row key={key}>
                                    <DataTable.Cell>{row.messageId}</DataTable.Cell>
                                    <DataTable.Cell>{row.text}</DataTable.Cell>
                                    <DataTable.Cell style={{ padding: 0 }}>
                                      {row.tokens.map((item, index) => {
                                        return (
                                          <div className={styles.token} key={index}>
                                            {item.name}
                                          </div>
                                        );
                                      })}
                                    </DataTable.Cell>
                                    <DataTable.Cell style={{ padding: 0 }}>
                                      {row.tokens.map((item, index) => {
                                        return (
                                          <div className={styles.token} key={index}>
                                            {item.description}
                                          </div>
                                        );
                                      })}
                                    </DataTable.Cell>
                                    <DataTable.Cell>
                                      <p>{getMessage(row.validationIssue)}</p>
                                    </DataTable.Cell>
                                  </DataTable.Row>
                                );
                              }}
                            </DataTable.Body>
                          </DataTable>
                        ) : (
                          <Form.FieldPair labelText={localizedValues.validationResult}>
                            <Text text={error.text ? error.text : ""}></Text>
                          </Form.FieldPair>
                        )}
                      </Form.Row>
                    </Form.Section>
                  </Form.Body>
                </Form>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide}></CancelLink>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { messagesUpload: IMessagesUploadState }) => {
    return {
      error: state.messagesUpload.error,
      notifications: state.messagesUpload.notifications
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        resetState: messagesUploadActions.resetUploadingMessages
      },
      dispatch
    );
  }
)(MessagesUploadResult);
